import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { RotateCcw, Maximize2, Expand } from "lucide-react";
import { Button } from "@/components/ui/button";
import { speak } from "@/lib/voice";
import { getRunnerSrc, getRunnerKey } from "@/lib/runners";
import api from "@/lib/api";

// Renders a game config inside the sandboxed Phaser runner iframe.
const GameCanvas = forwardRef(function GameCanvas({ config, previewConfig, onGameOver, onScore, onPos, onFrag, onReady, onAutosave, onShareLoadout, onNpcInteract, onPick, onZonePin, onCoopLoot, onCoopOut, onCoopProfile, onCoopClip, compact = false, previewMode = false }, ref) {
  const iframeRef = useRef(null);
  const [ready, setReady] = useState(false);
  const shotResolver = useRef(null);
  const sandboxResolver = useRef(null);
  const saveResolver = useRef(null);
  const effConfig = previewConfig || config;

  useEffect(() => {
    const handler = (ev) => {
      const d = ev.data || {};
      if (d.type === "ready") { setReady(true); if (onReady) onReady(); }
      if (d.type === "gameover" && onGameOver) onGameOver(d);
      if (d.type === "score" && onScore) onScore(d.score);
      if (d.type === "pos" && onPos) onPos(d);
      if (d.type === "frag" && onFrag) onFrag(d);
      if (d.type === "speak" && d.text) speak(String(d.text).slice(0, 400), d.voice || "onyx");
      if (d.type === "screenshot" && shotResolver.current) {
        shotResolver.current(d.data || null);
        shotResolver.current = null;
      }
      if (d.type === "sandbox-build" && sandboxResolver.current) {
        sandboxResolver.current(d.blocks || []);
        sandboxResolver.current = null;
      }
      if (d.type === "save-state-result" && saveResolver.current) {
        saveResolver.current(d.state || null);
        saveResolver.current = null;
      }
      if (d.type === "request-autosave" && onAutosave) onAutosave();
      if (d.type === "gv-share-loadout" && onShareLoadout) onShareLoadout(d.loadout || {});
      if (d.type === "npc_interact" && onNpcInteract) onNpcInteract(d);
      if (d.type === "gv-picked" && onPick) onPick(d.entity || null);
      if (d.type === "gv-zone-pin" && onZonePin) onZonePin(d.pos || null);
      if (d.type === "coop-loot" && onCoopLoot) onCoopLoot(d);
      if (d.type === "coop-out" && onCoopOut) onCoopOut(d.event || {});
      if ((d.type === "coop-profile" || d.type === "coop-squad-boss") && onCoopProfile) onCoopProfile(d);
      if (d.type === "coop-mvp-clip" && onCoopClip) onCoopClip(d);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [onGameOver, onScore, onPos, onFrag, onReady, onAutosave, onShareLoadout, onNpcInteract, onPick, onZonePin, onCoopLoot, onCoopOut, onCoopProfile, onCoopClip]);

  useImperativeHandle(ref, () => ({
    capture: () => new Promise((resolve) => {
      if (!iframeRef.current) return resolve(null);
      shotResolver.current = resolve;
      iframeRef.current.contentWindow.postMessage({ type: "screenshot" }, "*");
      setTimeout(() => { if (shotResolver.current) { shotResolver.current(null); shotResolver.current = null; } }, 1500);
    }),
    ghosts: (players) => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "ghosts", players }, "*");
    },
    coopIn: (payload) => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "coop-in", ...(payload || {}) }, "*");
    },
    setPaused: (paused) => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "setPaused", paused }, "*");
    },
    respawn: () => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "respawn" }, "*");
    },
    startRecording: (seconds = 15) => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "record-start", seconds }, "*");
    },
    recordTrailer: () => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "trailer-start" }, "*");
    },
    stopRecording: () => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "record-stop" }, "*");
    },
    exportSandbox: () => new Promise((resolve) => {
      if (!iframeRef.current) return resolve([]);
      sandboxResolver.current = resolve;
      iframeRef.current.contentWindow.postMessage({ type: "sandbox-export" }, "*");
      setTimeout(() => { if (sandboxResolver.current) { sandboxResolver.current([]); sandboxResolver.current = null; } }, 1500);
    }),
    loadSandbox: (blocks) => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "sandbox-load", blocks }, "*");
    },
    playCutscene: (name) => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "playCutscene", name }, "*");
    },
    saveState: () => new Promise((resolve) => {
      if (!iframeRef.current) return resolve(null);
      saveResolver.current = resolve;
      iframeRef.current.contentWindow.postMessage({ type: "save-state" }, "*");
      setTimeout(() => { if (saveResolver.current) { saveResolver.current(null); saveResolver.current = null; } }, 1500);
    }),
    loadState: (state) => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "load-state", state }, "*");
    },
    applyLoadout: (loadout) => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "gv-apply-loadout", loadout }, "*");
    },
    pickMode: (on) => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "gv-pick-mode", on: !!on }, "*");
    },
    zonePlaceMode: (on, keepPin = false) => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "gv-zone-place-mode", on: !!on, keepPin: !!keepPin }, "*");
    },
    botRun: (seconds = 45) => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "gv-bot-run", seconds }, "*");
    },
    botStop: () => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "gv-bot-stop" }, "*");
    },
    setGfx: (pref) => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "gv-set-gfx", pref }, "*");
    },
    highlightClip: (seconds = 10) => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "gv-highlight-clip", seconds }, "*");
    },
    annotateStart: (mode = "box") => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "gv-annotate-start", mode }, "*");
    },
    annotateMode: (mode) => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "gv-annotate-mode", mode }, "*");
    },
    annotateClear: () => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "gv-annotate-clear" }, "*");
    },
    annotateCancel: () => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "gv-annotate-cancel" }, "*");
    },
    annotateCapture: () => {
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "gv-annotate-capture" }, "*");
    },
  }));

  const sendLoad = () => {
    if (iframeRef.current && effConfig) {
      // previewMode (builder) skips the auto cinematic intro so it doesn't replay on every edit.
      const payload = previewMode ? { ...effConfig, __preview: true } : effConfig;
      iframeRef.current.contentWindow.postMessage({ type: "load", config: payload }, "*");
    }
  };

  const loadTimer = useRef(null);
  useEffect(() => {
    if (!ready) return;
    // Debounce rapid edits (e.g. slider drags, live tuning) into a single scene rebuild so the
    // preview updates smoothly instead of rebuilding on every keystroke/tick.
    clearTimeout(loadTimer.current);
    loadTimer.current = setTimeout(sendLoad, 220);
    return () => clearTimeout(loadTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, effConfig]);

  const restart = () => {
    if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "restart" }, "*");
  };

  // Account Memory: for a SIGNED-IN player, mint a short-lived play token so NPC memory + reputation
  // follow them across devices. Anonymous players fall back to a device id (handled in the runtime).
  const playTokenRef = useRef(null);
  useEffect(() => {
    const gid = effConfig?.liveNpc?.gameId;
    if (!gid || playTokenRef.current) return;
    let token = null;
    try { token = localStorage.getItem("gv_token"); } catch (e) {}
    if (!token) return;
    let cancelled = false;
    api.post("/gv2/play-token").then(({ data }) => {
      if (cancelled || !data?.token) return;
      playTokenRef.current = data.token;
      if (iframeRef.current) iframeRef.current.contentWindow.postMessage({ type: "gv-play-token", token: data.token }, "*");
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effConfig?.liveNpc?.gameId, ready]);

  const fullscreen = () => {
    if (iframeRef.current?.requestFullscreen) iframeRef.current.requestFullscreen();
  };
  const [theater, setTheater] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setTheater(false); };
    if (theater) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [theater]);

  const runnerSrc = getRunnerSrc(effConfig?.dimension);
  const runnerKey = getRunnerKey(effConfig?.dimension);

  const frameStyle = theater
    ? { aspectRatio: "16/9", height: "min(84vh, calc(94vw * 9 / 16))", width: "auto", maxWidth: "94vw", margin: "0 auto" }
    : { aspectRatio: "16/9" };

  return (
    <div className="w-full" data-testid="game-canvas">
      {theater && (
        <div className="fixed inset-0 z-[90] bg-black/88 backdrop-blur-md animate-in fade-in duration-200" onClick={() => setTheater(false)} data-testid="game-theater-backdrop" />
      )}
      <div className={theater ? "fixed z-[95] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-auto" : "relative w-full"}>
        <div
          className="relative rounded-2xl overflow-hidden bg-black shadow-[0_20px_70px_-15px_rgba(0,0,0,0.8)]"
          style={{ ...frameStyle, boxShadow: theater ? "0 30px 90px -20px rgba(0,0,0,0.9), 0 0 0 1px rgba(255,255,255,0.06)" : undefined }}
        >
          {/* premium gradient ring */}
          <div className="pointer-events-none absolute inset-0 z-[2] rounded-2xl ring-1 ring-white/10" style={{ boxShadow: "inset 0 0 0 1px rgba(59,158,255,0.25), inset 0 0 40px -10px rgba(59,158,255,0.35)" }} />
          <iframe
            ref={iframeRef}
            title="GameView Preview"
            src={runnerSrc}
            key={runnerKey}
            onLoad={sendLoad}
            sandbox="allow-scripts allow-same-origin"
            allow="fullscreen; gamepad; autoplay"
            allowFullScreen
            className="w-full h-full block relative z-[1]"
          />
          {theater && (
            <button onClick={() => setTheater(false)} data-testid="game-theater-close"
              className="absolute top-3 right-3 z-[3] w-9 h-9 grid place-items-center rounded-full bg-black/60 text-white/90 hover:bg-black/80 border border-white/15 backdrop-blur">
              ✕
            </button>
          )}
          {!ready && (
            <div className="absolute inset-0 z-[3] flex items-center justify-center bg-black/80 text-white">
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full border-2 border-white/20 border-t-[#3b9eff] animate-spin" />
                <p className="text-sm text-white/70">Loading engine…</p>
              </div>
            </div>
          )}
        </div>
        {!compact && (
          <div className="flex items-center justify-center gap-2 mt-3">
            <Button variant="secondary" size="sm" onClick={restart} data-testid="game-restart-btn">
              <RotateCcw className="w-4 h-4" /> Restart
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setTheater((t) => !t)} data-testid="game-theater-btn">
              <Expand className="w-4 h-4" /> {theater ? "Exit theater" : "Theater"}
            </Button>
            <Button variant="secondary" size="sm" onClick={fullscreen} data-testid="game-fullscreen-btn">
              <Maximize2 className="w-4 h-4" /> Fullscreen
            </Button>
          </div>
        )}
      </div>
    </div>
  );
});

export default GameCanvas;
